<script setup lang="ts">
/** Section 1 of the order form: the POS customer of this chat — linked card, search, or "add new". `locked`: the edit of a
 *  held order ("แก้ไขออเดอร์") — its customer cannot change (another customer = a new order).
 *  A linked customer with no pin on a Lalamove order gets the "ใส่หมุด" field (owner 18 ต.ค. 69 "บังคับหมุด ใส่หมุดได้จากแชท"):
 *  prefilled from the place the customer gave in this chat (never guessed), saved to the POS customer (pin only). */
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { PosApiError } from '../../../api/pos'
import type { PosCustomer, PosSharedPlace, PosShipping } from '../../../api/pos/types'
import type { PrefillAddress } from '../../../api/script/types'
import { parsePin } from '../../../lib/order'
import { useOrders } from '../../../stores/order'
import Icon from '../../ui/Icon.vue'

const props = defineProps<{ convId: string; contactName: string; locked?: boolean }>()
const orders = useOrders()

const f = computed(() => orders.forms[props.convId] ?? null)
const ctx = computed(() => orders.contexts[props.convId] ?? null)
const cid = computed(() => f.value?.customerId ?? null)
const customer = computed(() => (cid.value ? (orders.customers[cid.value] ?? null) : null))
const hist = computed(() => (cid.value ? (orders.histories[cid.value] ?? null) : null))

const changing = ref(false)
const showPicker = computed(() => !props.locked && (!cid.value || changing.value))

// ------------------------------------------------ search
const q = ref('')
const hits = ref<PosCustomer[] | null>(null)
const searching = ref(false)
const searchErr = ref<string | null>(null)
const busy = ref<string | null>(null)
const linkErr = ref<string | null>(null)
let seq = 0
let timer: ReturnType<typeof setTimeout> | null = null

watch(q, (v) => {
  if (timer) clearTimeout(timer)
  const t = v.trim()
  if (t.length < 2) {
    hits.value = null
    searching.value = false
    return
  }
  searching.value = true
  timer = setTimeout(() => void runSearch(t), 300)
})
onBeforeUnmount(() => {
  if (timer) clearTimeout(timer)
})

async function runSearch(t: string): Promise<void> {
  const s = ++seq
  try {
    const r = await orders.searchCustomers(t)
    if (s !== seq) return
    hits.value = r
    searchErr.value = null
  } catch (e) {
    if (s === seq) searchErr.value = e instanceof Error ? e.message : String(e)
  } finally {
    if (s === seq) searching.value = false
  }
}

async function pick(c: PosCustomer): Promise<void> {
  busy.value = c.id
  linkErr.value = null
  try {
    await orders.linkCustomer(props.convId, c)
    changing.value = false
    creating.value = false
    q.value = ''
    hits.value = null
  } catch (e) {
    linkErr.value = e instanceof Error ? e.message : String(e)
  } finally {
    busy.value = null
  }
}

// ------------------------------------------------ create
const creating = ref(false)
const nf = ref({
  fname: '',
  lname: '',
  mobile: '',
  shipping: 'lalamove' as PosShipping,
  address: '',
  pin: '',
  line: '',
  tambon: '',
  amphoe: '',
  province: '',
  postcode: '',
})
const createErr = ref<string | null>(null)
const existing = ref<PosCustomer | null>(null)
const createWarn = ref<string[]>([])
const saving = ref(false)

const pin = computed(() => parsePin(nf.value.pin))
const pinBad = computed(() => nf.value.pin.trim() !== '' && !pin.value)
const mobileDigits = computed(() => nf.value.mobile.replace(/\D/g, ''))
const mobileBad = computed(() => nf.value.mobile.trim() !== '' && !/^0\d{8,9}$/.test(mobileDigits.value))
const canCreate = computed(() => {
  const n = nf.value
  if (!n.fname.trim() || !/^0\d{8,9}$/.test(mobileDigits.value) || pinBad.value) return false
  // Lalamove needs the pin (owner 18 ต.ค. 69 "ถ้า lalamove ยังไงต้องมีหมุด"): the fee and the rider's stop come from it
  if (n.shipping === 'lalamove') return n.address.trim().length >= 5 && !!pin.value
  return !!(n.line.trim() && n.tambon.trim() && n.amphoe.trim() && n.province.trim() && /^\d{5}$/.test(n.postcode.trim()))
})

/** The first name filled from the chat's display name (not typed by the staff — the chat's own name may replace it). */
const chatName = computed(() => (props.contactName ?? '').replace(/^คุณ\s*/, '').slice(0, 60))

function openCreate(mobile?: string, keep = false): void {
  creating.value = true
  createErr.value = null
  existing.value = null
  if (!nf.value.fname && props.contactName) nf.value.fname = chatName.value
  if (mobile && !(keep && nf.value.mobile.trim())) nf.value.mobile = mobile
  const digits = q.value.replace(/\D/g, '')
  if (!nf.value.mobile && /^0\d{8,9}$/.test(digits)) nf.value.mobile = digits
}

/** The place the chat gave (the sales steps' summary handoff): fills the empty address fields only — what the staff
 *  typed stays. Lalamove: the customer's words + the shared pin · DHL: the words as the first line + อำเภอ/จังหวัด/รหัส. */
function fillPlace(a: PrefillAddress, shipping: PosShipping | null | undefined, keep = false): void {
  const n = nf.value
  // "เติมจากแชททั้งหมด" (keep): the carrier only while the staff typed no address of either kind
  if (shipping && !(keep && (n.address.trim() || n.line.trim()))) n.shipping = shipping
  const text = (a.text ?? '').trim()
  if (!n.address.trim() && text) n.address = text
  if (!n.pin.trim() && a.pin) n.pin = `${a.pin.lat}, ${a.pin.lng}`
  if (!n.line.trim() && text) n.line = text
  if (!n.tambon.trim() && a.tambon) n.tambon = a.tambon
  if (!n.amphoe.trim() && a.district) n.amphoe = a.district
  if (!n.province.trim() && a.province) n.province = a.province
  if (!n.postcode.trim() && a.postcode) n.postcode = a.postcode
}

// "สร้างลูกค้าใหม่" pressed on the phone button under a customer's message (components/chat/PhoneLink.vue):
// the same form, opened here with that phone (and the chat name as first name) · "เปิดลงออเดอร์ (เติมให้แล้ว)":
// with the place from the chat
watch(
  () => [orders.createRequests[props.convId]?.at ?? 0, !!props.locked] as const,
  ([at, locked]) => {
    const req = orders.createRequests[props.convId]
    if (!at || !req || locked) return // an edit open: the request waits for the new-order form
    orders.createRequests[props.convId] = null
    if (cid.value) changing.value = true
    openCreate(req.mobile || undefined, !!req.keep)
    if (req.address) fillPlace(req.address, req.shipping, !!req.keep)
    // the recipient's name from the chat: over an empty name or the display name only (never the staff's typing)
    const name = (req.name ?? '').trim().split(/\s+/)
    if (name[0] && (!nf.value.fname.trim() || nf.value.fname === chatName.value) && !nf.value.lname.trim()) {
      nf.value.fname = name[0].slice(0, 60)
      nf.value.lname = name.slice(1).join(' ').slice(0, 60)
    }
  },
  { immediate: true },
)

async function create(): Promise<void> {
  if (!canCreate.value || saving.value) return
  saving.value = true
  createErr.value = null
  existing.value = null
  const n = nf.value
  try {
    const res = await orders.createCustomer(props.convId, {
      fname: n.fname.trim(),
      lname: n.lname.trim(),
      mobile: mobileDigits.value,
      shipping: n.shipping,
      address: n.shipping === 'lalamove' ? n.address.trim() : null,
      lat: n.shipping === 'lalamove' ? (pin.value?.lat ?? null) : null,
      lng: n.shipping === 'lalamove' ? (pin.value?.lng ?? null) : null,
      dhl:
        n.shipping === 'dhl'
          ? { address: n.line.trim(), tambon: n.tambon.trim(), amphoe: n.amphoe.trim(), province: n.province.trim(), postcode: n.postcode.trim() }
          : null,
    })
    createWarn.value = res.warnings
    creating.value = false
    changing.value = false
    q.value = ''
  } catch (e) {
    if (e instanceof PosApiError && e.reason === 'mobileExists' && e.extra.existing) {
      existing.value = e.extra.existing as PosCustomer
    }
    createErr.value = e instanceof Error ? e.message : String(e)
  } finally {
    saving.value = false
  }
}

// ------------------------------------------------ หมุด Lalamove ของลูกค้าเก่า
/** No pin and the order ships Lalamove: the field shows (also at once when the staff switch the carrier to Lalamove). */
const needPin = computed(() => !!customer.value && customer.value.has_pin === false && f.value?.shipping === 'lalamove')
const pinText = ref('')
/** The customer the typed pin belongs to (another customer: the field starts empty). */
const pinFor = ref<string | null>(null)
/** The chat place the field was filled from (its note shows while the text is still that place). */
const pinFrom = ref<PosSharedPlace | null>(null)
const pinSaving = ref(false)
const pinErr = ref<string | null>(null)
/** After "บันทึกหมุด": the customer it was saved for + the POS warnings (e.g. no road distance). */
const pinDone = ref<{ id: string; warnings: string[] } | null>(null)
const pinParsed = computed(() => parsePin(pinText.value))
const pinWrong = computed(() => pinText.value.trim() !== '' && !pinParsed.value)
const pinShortLink = computed(() => pinWrong.value && /maps\.app\.goo\.gl|goo\.gl\/maps/i.test(pinText.value))
const place = computed(() => orders.sharedPlaces[props.convId]?.data ?? null)
const placeText = (p: PosSharedPlace) => `${p.lat}, ${p.lng}`
const fromChat = computed(() => !!pinFrom.value && pinText.value.trim() === placeText(pinFrom.value))
const fmtAt = new Intl.DateTimeFormat('th-TH', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Bangkok' })
const placeWhen = computed(() => {
  const t = pinFrom.value?.at ? Date.parse(pinFrom.value.at) : NaN
  return Number.isNaN(t) ? '' : ` (${fmtAt.format(new Date(t))} น.)`
})

watch(
  () => [needPin.value, cid.value] as const,
  ([on, id]) => {
    if (id !== pinFor.value) {
      pinFor.value = id
      pinText.value = ''
      pinFrom.value = null
      pinErr.value = null
    }
    if (on) void orders.loadSharedPin(props.convId)
  },
  { immediate: true },
)
// the customer's own place from the chat → an EMPTY field only (what the staff typed stays)
watch(
  () => [place.value, needPin.value] as const,
  ([p, on]) => {
    if (!p || !on || pinText.value.trim()) return
    pinText.value = placeText(p)
    pinFrom.value = p
  },
  { immediate: true },
)

async function savePin(): Promise<void> {
  const p = pinParsed.value
  const id = cid.value
  if (!p || !id || pinSaving.value) return
  pinSaving.value = true
  pinErr.value = null
  try {
    const res = await orders.setPin(props.convId, id, p)
    pinDone.value = { id, warnings: res.warnings }
    pinText.value = ''
    pinFrom.value = null
  } catch (e) {
    pinErr.value = e instanceof Error ? e.message : String(e)
  } finally {
    pinSaving.value = false
  }
}

async function unlink(): Promise<void> {
  busy.value = 'unlink'
  try {
    await orders.unlinkCustomer(props.convId)
    changing.value = false
  } catch (e) {
    linkErr.value = e instanceof Error ? e.message : String(e)
  } finally {
    busy.value = null
  }
}
</script>

<template>
  <section class="os" aria-label="ลูกค้าใน POS">
    <div class="os-h">
      <span class="os-n">1</span><h3 class="os-t">ลูกค้าใน POS</h3><span class="grow"></span>
      <span v-if="locked" class="lockc" aria-label="ลูกค้าของพักบิลนี้ เปลี่ยนไม่ได้"><Icon name="lock" />ลูกค้าเดิม</span>
      <button v-else-if="cid && !changing" class="tlink" @click="changing = true"><Icon name="swap" />เปลี่ยนลูกค้า</button>
      <button v-else-if="cid && changing" class="tlink" @click="changing = false"><Icon name="x" />ยกเลิก</button>
    </div>

    <template v-if="cid && !changing">
      <div v-if="customer" class="cuc">
        <div class="cuc-1">
          <span class="cuc-n">{{ customer.name }}</span>
          <span v-if="customer.mobile_masked" class="mono cuc-m">{{ customer.mobile_masked }}</span>
          <span class="grow"></span>
          <span class="mono cuc-id">POS #{{ customer.id }}</span>
        </div>
        <p class="cuc-a">{{ customer.address || 'ยังไม่มีที่อยู่ใน POS' }}</p>
        <div class="cuc-3">
          <span v-if="customer.has_pin === true" class="chip ok"
            ><svg class="ic" viewBox="0 0 24 24" aria-hidden="true"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" /><circle cx="12" cy="10" r="3" /></svg
            >มีหมุด Lalamove</span
          >
          <span v-else-if="customer.has_pin === false" class="chip warn"
            ><svg class="ic" viewBox="0 0 24 24" aria-hidden="true"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" /><circle cx="12" cy="10" r="3" /></svg
            >ยังไม่มีหมุด</span
          >
          <span v-if="pinDone?.id === customer.id && customer.has_pin" class="note">บันทึกหมุดใน POS แล้ว</span>
          <span class="note">ที่อยู่จาก POS · แก้ที่หน้าลูกค้าใน POS</span>
        </div>
        <template v-if="pinDone?.id === customer.id">
          <p v-for="w in pinDone.warnings" :key="w" class="note">{{ w }}</p>
        </template>
        <div v-if="needPin" class="pinf" role="group" aria-label="ใส่หมุด Lalamove ของลูกค้า">
          <p class="pinf-h">ส่ง Lalamove ต้องมีหมุด ใส่หมุดแล้วกดบันทึก ระบบบันทึกเฉพาะหมุดลงลูกค้าใน POS ที่อยู่เดิมไม่เปลี่ยน</p>
          <div class="pinf-r">
            <span class="inp" :class="{ bad: pinWrong }"
              ><input
                v-model="pinText"
                type="text"
                aria-label="หมุดลูกค้า พิกัด หรือลิงก์ Google Maps"
                placeholder="13.7563, 100.5018 หรือลิงก์ Google Maps"
                @keydown.enter.prevent="savePin"
            /></span>
            <button class="btn-s" :disabled="!pinParsed || pinSaving" @click="savePin">
              <span v-if="pinSaving" class="spin"></span><Icon v-else name="save" />บันทึกหมุด
            </button>
          </div>
          <p v-if="fromChat && pinFrom" class="note">
            เติมจาก{{ pinFrom.source === 'link' ? 'ลิงก์ Google Maps ที่ลูกค้าส่งในแชท' : 'ตำแหน่งที่ลูกค้าแชร์ในแชท' }}{{ placeWhen
            }}{{ pinFrom.address ? ` · ${pinFrom.address}` : '' }} ตรวจกับที่อยู่ก่อนบันทึก
          </p>
          <p v-else-if="pinShortLink" class="err">
            <Icon name="alertCircle" /><span>ลิงก์แบบย่ออ่านพิกัดในช่องนี้ไม่ได้ เปิดลิงก์แล้วคัดลอกพิกัด หรือขอให้ลูกค้าแชร์ตำแหน่งในแชท</span>
          </p>
          <p v-else-if="pinWrong" class="err">
            <Icon name="alertCircle" /><span>อ่านพิกัดไม่ออก ใช้รูปแบบ 13.7563, 100.5018 หรือวางลิงก์ Google Maps</span>
          </p>
          <p v-else-if="!pinText.trim()" class="note">ยังไม่มีตำแหน่งจากลูกค้าในแชทนี้ ขอให้ลูกค้าแชร์ตำแหน่ง แล้ววางพิกัดหรือลิงก์ Google Maps ที่นี่ หรือเปลี่ยนเป็นส่ง DHL</p>
          <p v-if="pinErr" class="err"><Icon name="alertCircle" /><span>{{ pinErr }}</span></p>
        </div>
        <p v-if="locked" class="note">แก้ไขพักบิลเปลี่ยนลูกค้าไม่ได้ ถ้าเป็นลูกค้าคนอื่น ให้ยกเลิกการแก้ไขแล้วลงออเดอร์ใหม่</p>
        <p v-for="w in createWarn" :key="w" class="err"><Icon name="alertCircle" /><span>{{ w }}</span></p>
      </div>
      <div v-else-if="hist?.state === 'error'" class="err">
        <Icon name="alertCircle" /><span>โหลดข้อมูลลูกค้า POS #{{ cid }} ไม่สำเร็จ: {{ hist.error }}</span>
        <button class="tlink" @click="orders.loadHistory(cid)">ลองใหม่</button>
      </div>
      <p v-else class="note"><span class="spin"></span> กำลังโหลดลูกค้า POS #{{ cid }}</p>
    </template>

    <template v-if="showPicker">
      <p v-if="ctx && !ctx.can_link" class="note">ห้องนี้เป็นกลุ่ม ลงออเดอร์ได้ แต่ระบบจะไม่จำว่าแชทนี้คือลูกค้าคนไหน</p>
      <label class="inp"
        ><Icon name="search" /><input
          v-model="q"
          type="search"
          aria-label="ค้นหาลูกค้าใน POS ด้วยเบอร์หรือชื่อ"
          placeholder="ค้นหาลูกค้าด้วยเบอร์หรือชื่อ"
          autocomplete="off"
        /><span v-if="searching" class="spin"></span
      ></label>
      <p v-if="searchErr" class="err"><Icon name="alertCircle" /><span>{{ searchErr }}</span></p>
      <div v-if="hits && hits.length" class="cul" role="list" aria-label="ผลค้นหาลูกค้า">
        <button
          v-for="c in hits"
          :key="c.id"
          class="cur"
          role="listitem"
          :disabled="busy !== null"
          :aria-label="`เลือก ${c.name} ${c.mobile_masked ?? ''}`"
          @click="pick(c)"
        >
          <span class="cur-1"
            ><b>{{ c.name }}</b><span v-if="c.mobile_masked" class="mono">{{ c.mobile_masked }}</span><span class="grow"></span
            ><span v-if="busy === c.id" class="spin"></span><span v-else class="cur-go">เลือก</span></span
          >
          <span v-if="c.address" class="cur-a">{{ c.address }}</span>
        </button>
      </div>
      <p v-else-if="hits && !searching" class="note">ไม่พบลูกค้า เพิ่มเป็นลูกค้าใหม่ได้</p>
      <p v-if="linkErr" class="err"><Icon name="alertCircle" /><span>{{ linkErr }}</span></p>
      <div v-if="!creating" class="cu-acts">
        <button class="tlink ac" @click="openCreate()"><Icon name="plus" />เพิ่มลูกค้าใหม่ใน POS</button>
        <span class="grow"></span>
        <button v-if="cid && changing" class="tlink" :disabled="busy !== null" @click="unlink">เลิกผูกลูกค้ากับแชทนี้</button>
      </div>

      <div v-if="creating" class="nc" aria-label="เพิ่มลูกค้าใหม่ใน POS">
        <div class="grid2">
          <label class="fl"
            ><span class="fl-l">ชื่อ</span><span class="inp"><input v-model="nf.fname" type="text" aria-label="ชื่อ" /></span
          ></label>
          <label class="fl"
            ><span class="fl-l">นามสกุล (ไม่บังคับ)</span><span class="inp"><input v-model="nf.lname" type="text" aria-label="นามสกุล" /></span
          ></label>
        </div>
        <label class="fl"
          ><span class="fl-l">เบอร์โทร</span
          ><span class="inp" :class="{ bad: mobileBad }"
            ><input v-model="nf.mobile" type="tel" inputmode="tel" aria-label="เบอร์โทรลูกค้า" placeholder="08xxxxxxxx" /></span
        ></label>
        <div class="seg" role="radiogroup" aria-label="ขนส่งของที่อยู่นี้">
          <button class="seg-b" :class="{ 'is-on': nf.shipping === 'lalamove' }" role="radio" :aria-checked="nf.shipping === 'lalamove'" @click="nf.shipping = 'lalamove'">
            <Icon name="truck" />Lalamove
          </button>
          <button class="seg-b" :class="{ 'is-on': nf.shipping === 'dhl' }" role="radio" :aria-checked="nf.shipping === 'dhl'" @click="nf.shipping = 'dhl'">
            <Icon name="globe" />DHL ต่างจังหวัด
          </button>
        </div>
        <template v-if="nf.shipping === 'lalamove'">
          <label class="fl"
            ><span class="fl-l">ที่อยู่</span
            ><textarea v-model="nf.address" class="ta" rows="2" aria-label="ที่อยู่จัดส่ง Lalamove"></textarea
          ></label>
          <label class="fl"
            ><span class="fl-l">หมุด · พิกัด หรือลิงก์ Google Maps</span
            ><span class="inp" :class="{ bad: pinBad }"
              ><input v-model="nf.pin" type="text" aria-label="หมุด พิกัด หรือลิงก์ Google Maps" placeholder="13.7563, 100.5018" /></span
          ></label>
          <p v-if="pinBad" class="err"><Icon name="alertCircle" /><span>อ่านพิกัดไม่ออก ใช้รูปแบบ 13.7563, 100.5018 หรือวางลิงก์ Google Maps</span></p>
          <p v-else-if="!pin" class="note">Lalamove ต้องมีหมุด ขอให้ลูกค้าแชร์ตำแหน่งในแชท แล้ววางพิกัดหรือลิงก์ Google Maps ที่นี่</p>
        </template>
        <template v-else>
          <label class="fl"
            ><span class="fl-l">บ้านเลขที่ หมู่ ถนน</span><span class="inp"><input v-model="nf.line" type="text" aria-label="บ้านเลขที่ หมู่ ถนน" /></span
          ></label>
          <div class="grid2">
            <label class="fl"
              ><span class="fl-l">ตำบล/แขวง</span><span class="inp"><input v-model="nf.tambon" type="text" aria-label="ตำบล" /></span
            ></label>
            <label class="fl"
              ><span class="fl-l">อำเภอ/เขต</span><span class="inp"><input v-model="nf.amphoe" type="text" aria-label="อำเภอ" /></span
            ></label>
          </div>
          <div class="grid2">
            <label class="fl"
              ><span class="fl-l">จังหวัด</span><span class="inp"><input v-model="nf.province" type="text" aria-label="จังหวัด" /></span
            ></label>
            <label class="fl"
              ><span class="fl-l">รหัสไปรษณีย์</span
              ><span class="inp"><input v-model="nf.postcode" type="text" inputmode="numeric" maxlength="5" aria-label="รหัสไปรษณีย์" /></span
            ></label>
          </div>
          <p class="note">ตำบล อำเภอ จังหวัด ต้องตรงกับรหัสไปรษณีย์ในตาราง POS</p>
        </template>
        <div v-if="createErr" class="err">
          <Icon name="alertCircle" /><span>{{ createErr }}</span>
        </div>
        <div v-if="existing" class="cur exist">
          <span class="cur-1"
            ><b>{{ existing.name }}</b><span v-if="existing.mobile_masked" class="mono">{{ existing.mobile_masked }}</span></span
          >
          <span v-if="existing.address" class="cur-a">{{ existing.address }}</span>
          <button class="btn-s" :disabled="busy !== null" @click="pick(existing)"><Icon name="check" />ใช้ลูกค้าคนนี้</button>
        </div>
        <div class="cu-acts">
          <button class="btn-s" :disabled="!canCreate || saving" @click="create">
            <span v-if="saving" class="spin"></span><Icon v-else name="save" />บันทึกลูกค้าใน POS
          </button>
          <button class="tlink" @click="creating = false">ยกเลิก</button>
        </div>
      </div>
    </template>
  </section>
</template>

<style scoped>
.cuc {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px 12px;
  border: 1px solid #cfe3d8;
  border-radius: 10px;
  background: #f7fbf9;
}
.cuc-1 {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 2px 8px;
}
.cuc-n {
  font-size: 14px;
  font-weight: 600;
  line-height: 22px;
}
.cuc-m,
.cuc-id {
  font-size: 12px;
  line-height: 18px;
  color: var(--mut);
}
.cuc-a {
  font-size: 12.5px;
  line-height: 19px;
  color: #3e4d46;
}
.cuc-3 {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 8px;
  margin-top: 2px;
}
.cul {
  display: flex;
  flex-direction: column;
  border: 1px solid var(--line);
  border-radius: 10px;
  overflow: hidden;
}
.cur {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 12px;
  background: #fff;
  text-align: left;
}
.cur + .cur {
  border-top: 1px solid var(--line2);
}
.cur:hover:not(:disabled) {
  background: #f5faf7;
}
.cur-1 {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 2px 8px;
  font-size: 13px;
  line-height: 20px;
}
.cur-1 b {
  font-weight: 600;
}
.cur-1 .mono {
  font-size: 12px;
  line-height: 18px;
  color: var(--mut);
}
.cur-go {
  font-size: 12px;
  line-height: 18px;
  font-weight: 600;
  color: var(--ac);
}
.cur-a {
  font-size: 12px;
  line-height: 18px;
  color: var(--mut);
}
.cur.exist {
  border: 1px solid #cfe3d8;
  border-radius: 10px;
  background: #f7fbf9;
  gap: 6px;
}
.cur.exist .btn-s {
  align-self: flex-start;
}
.cu-acts {
  display: flex;
  align-items: center;
  gap: 8px;
}
.pinf {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 6px;
  padding: 8px 10px;
  border: 1px solid #f0dcae;
  border-radius: 10px;
  background: #fffaf0;
}
.pinf-h {
  font-size: 12.5px;
  line-height: 19px;
  color: #7a4700;
}
.pinf-r {
  display: flex;
  align-items: center;
  gap: 8px;
}
.pinf-r .inp {
  flex: 1;
}
.nc {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px;
  border: 1px solid var(--line);
  border-radius: 12px;
  background: #fbfcfb;
}
</style>
